import { subscribeToBlockDeltas, subscribeToBlockStop } from "@storytell/streaming";
import { AddToCanvasMenu } from "@storytell/ui/transcript-media";
import { TbChevronRight, TbPhoto } from "solid-icons/tb";
import { createEffect, createMemo, createSignal, onCleanup, onMount, Show } from "solid-js";
import { type MediaStorageLocation, useChatRuntime } from "../runtime/ChatRuntime";
import { ProcessingSpinner } from "../ui";
import { sanitizeForFilename } from "../util/download";
import { MediaDownloadButton } from "./MediaDownloadButton";
import { useSignedImageUrl } from "./useSignedImageUrl";

interface ImageGenerationRendererProps {
  blockId: string;
}

interface ImageData {
  type: "image";
  storageLocation?: MediaStorageLocation;
  metadata?: {
    model?: string;
    revisedPrompt?: string;
    prompt?: string;
    aspectRatio?: string;
  };
}

/**
 * Inline chat-thread card for generated images. Hosts with a content viewer
 * (bluesky) get a compact card that opens it via `openContentBlock`; hosts
 * without one (chief) render the image itself inline through the
 * `resolveImageUrls` seam.
 */
export function ImageGenerationRenderer(props: ImageGenerationRendererProps) {
  const rt = useChatRuntime();

  const [imageData, setImageData] = createSignal<ImageData | null>(null);
  const [isStreaming, setIsStreaming] = createSignal(true);

  // Reactive data loading - picks up data even if block populates after mount
  createEffect(() => {
    const blockData = rt.store.block(props.blockId);
    if (!blockData) return;

    if (blockData.imageResults) {
      const results = blockData.imageResults;
      setImageData({
        type: "image",
        storageLocation: results.storageLocation
          ? { bucket: results.storageLocation.bucket, key: results.storageLocation.key }
          : undefined,
        metadata: results.metadata
          ? {
              model: results.metadata.model,
              revisedPrompt: results.metadata.revisedPrompt,
              prompt: results.metadata.prompt,
              aspectRatio: results.metadata.aspectRatio,
            }
          : undefined,
      });
      setIsStreaming(false);
      return;
    }

    if (blockData.finalContent?.type === "text" && blockData.finalContent.text) {
      try {
        const parsed = JSON.parse(blockData.finalContent.text) as ImageData;
        if (parsed.type === "image") {
          setImageData(parsed);
          setIsStreaming(false);
          return;
        }
      } catch {
        // Not JSON
      }
    }
  });

  // Delta subscription fallback for streaming blocks
  onMount(() => {
    const blockData = rt.store.block(props.blockId);
    if (blockData?.imageResults || (blockData?.finalContent?.type === "text" && blockData.finalContent.text)) {
      return;
    }

    const unsubscribeDelta = subscribeToBlockDeltas(props.blockId, (delta: string) => {
      try {
        const parsed = JSON.parse(delta) as ImageData;
        if (parsed.type === "image") {
          setImageData(parsed);
        }
      } catch (error) {
        console.warn("Failed to parse image data:", error);
      }
    });

    const unsubscribeStop = subscribeToBlockStop(props.blockId, () => {
      setIsStreaming(false);
    });

    onCleanup(() => {
      unsubscribeDelta();
      unsubscribeStop();
    });
  });

  const storageLocation = () => imageData()?.storageLocation;

  const title = createMemo(() => {
    const d = imageData();
    const prompt = d?.metadata?.revisedPrompt || d?.metadata?.prompt;
    if (prompt) return prompt.slice(0, 60) + (prompt.length > 60 ? "..." : "");
    return rt.t("activity.image_generation");
  });

  const subtitle = createMemo(() => {
    const label = rt.t("entity.image");
    const ar = imageData()?.metadata?.aspectRatio;
    return ar ? `${label} · ${ar}` : label;
  });

  const canOpen = () => Boolean(rt.actions.openContentBlock);
  const showInline = () =>
    Boolean(rt.actions.resolveImageUrls) && (rt.caps.inlineMedia || !rt.actions.openContentBlock);

  const handleClick = () => {
    rt.actions.openContentBlock?.(props.blockId);
  };

  return (
    <div data-renderer="image-generation-renderer">
      <Show
        when={storageLocation()}
        fallback={
          <Show when={isStreaming()}>
            <div class="flex items-center gap-3 py-2 text-sm text-secondary-text">
              <ProcessingSpinner class="size-4" />
              <span>{rt.t("generating_image")}</span>
            </div>
          </Show>
        }
      >
        {(location) => (
          <Show
            when={!showInline()}
            fallback={<InlineGeneratedImage location={location()} title={title()} subtitle={subtitle()} />}
          >
            <button
              type="button"
              onClick={handleClick}
              disabled={!canOpen()}
              class="flex w-full items-center gap-3 rounded-lg border border-primary-border/50 p-3 text-left transition-colors enabled:cursor-pointer hover:enabled:bg-canvas-hover"
            >
              <div class="flex size-10 shrink-0 items-center justify-center rounded-lg bg-inset">
                <TbPhoto class="size-5 text-secondary-text" />
              </div>
              <div class="min-w-0 flex-1">
                <div class="truncate text-sm font-medium text-primary-text">{title()}</div>
                <div class="mt-0.5 text-xs text-muted-text">{subtitle()}</div>
              </div>
              <Show when={canOpen()}>
                <TbChevronRight class="size-4 shrink-0 text-muted-text" />
              </Show>
            </button>
          </Show>
        )}
      </Show>
    </div>
  );
}

const InlineGeneratedImage = (props: { location: MediaStorageLocation; title: string; subtitle: string }) => {
  const rt = useChatRuntime();
  const signed = useSignedImageUrl(() => props.location);
  const filename = () => `${sanitizeForFilename(props.title).slice(0, 80) || "image"}.png`;

  return (
    <div class="overflow-hidden rounded-lg border border-primary-border/50">
      <Show when={signed.loading()}>
        <div class="flex items-center justify-center bg-inset p-8">
          <ProcessingSpinner class="size-6" />
        </div>
      </Show>
      <Show when={signed.error()}>
        <div class="flex items-center gap-2 p-3 text-sm text-secondary-text">
          <TbPhoto class="size-4 shrink-0" />
          <span>{rt.t("error_failed_to_load_image")}</span>
        </div>
      </Show>
      <Show when={signed.url()}>
        <img src={signed.url()} alt={props.title} class="block max-h-96 w-full bg-inset object-contain" />
      </Show>
      <div class="flex items-start justify-between gap-2 px-3 py-2">
        <div class="min-w-0">
          <div class="truncate text-sm font-medium text-primary-text">{props.title}</div>
          <div class="mt-0.5 text-xs text-muted-text">{props.subtitle}</div>
        </div>
        <div class="flex shrink-0 items-center gap-1">
          <Show when={rt.actions.downloadMedia}>
            <MediaDownloadButton location={props.location} filename={filename()} />
          </Show>
          <Show when={rt.actions.addToCanvas?.targets()}>
            {(targets) => (
              <AddToCanvasMenu
                targets={targets()}
                onAdd={(id) => {
                  rt.actions.trackFeature?.({ feature: "chat.add_to_canvas", via: "menu" });
                  return rt.actions.addToCanvas?.add({ location: props.location, filename: filename(), targetId: id });
                }}
                triggerClass="group/btn shrink-0 shadow-md cursor-pointer flex items-center justify-center rounded-lg bg-tile hover:bg-tile-hover border border-muted-border transition-all p-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              />
            )}
          </Show>
        </div>
      </div>
    </div>
  );
};
